const INQUIRY_SELECTION_KEY = "resourcehub:selected-resources";
const INQUIRY_SELECTION_CHANGED_EVENT = "resourcehub:inquiry-selection-changed";

const emitSelectionChanged = (ids) => {
	window.dispatchEvent(
		new CustomEvent(INQUIRY_SELECTION_CHANGED_EVENT, {
			detail: { ids },
		}),
	);
};

const normalizeSelection = (value) => {
	const id = Number(value?.id ?? value?.resourceId ?? value);
	const comment = String(value?.comment ?? "").trim();

	if (!Number.isInteger(id) || id <= 0) {
		return null;
	}

	return { id, comment };
};

export const getSelectedResources = () => {
	try {
		const raw = localStorage.getItem(INQUIRY_SELECTION_KEY);
		if (!raw) {
			return [];
		}

		const parsed = JSON.parse(raw);
		if (!Array.isArray(parsed)) {
			return [];
		}

		const normalized = parsed
			.map((value) => normalizeSelection(value))
			.filter(Boolean);

		const deduped = [];
		const seen = new Set();
		normalized.forEach((item) => {
			if (!seen.has(item.id)) {
				deduped.push(item);
				seen.add(item.id);
			}
		});

		return deduped;
	} catch {
		return [];
	}
};

export const getSelectedResourceIds = () => {
	return getSelectedResources().map((item) => item.id);
};

export const setSelectedResourceIds = (ids) => {
	const normalized = Array.from(
		new Set((ids || []).map((value) => Number(value))),
	)
		.filter((value) => Number.isInteger(value) && value > 0)
		.map((id) => ({ id, comment: "" }));

	localStorage.setItem(INQUIRY_SELECTION_KEY, JSON.stringify(normalized));
	emitSelectionChanged(normalized.map((item) => item.id));
	return normalized.map((item) => item.id);
};

export const setSelectedResources = (items) => {
	const normalized = [];
	const seen = new Set();

	(items || []).forEach((item) => {
		const parsed = normalizeSelection(item);
		if (!parsed || seen.has(parsed.id)) {
			return;
		}
		normalized.push(parsed);
		seen.add(parsed.id);
	});

	localStorage.setItem(INQUIRY_SELECTION_KEY, JSON.stringify(normalized));
	emitSelectionChanged(normalized.map((item) => item.id));
	return normalized;
};

export const upsertSelectedResource = (resourceId, comment) => {
	const id = Number(resourceId);
	if (!Number.isInteger(id) || id <= 0) {
		return getSelectedResources();
	}

	const normalizedComment = String(comment || "").trim();
	const current = getSelectedResources();
	const existingIndex = current.findIndex((item) => item.id === id);

	if (existingIndex >= 0) {
		current[existingIndex] = { id, comment: normalizedComment };
	} else {
		current.push({ id, comment: normalizedComment });
	}

	return setSelectedResources(current);
};

export const removeSelectedResource = (resourceId) => {
	const id = Number(resourceId);
	if (!Number.isInteger(id) || id <= 0) {
		return getSelectedResources();
	}

	const next = getSelectedResources().filter((item) => item.id !== id);
	return setSelectedResources(next);
};

export const toggleSelectedResourceId = (resourceId) => {
	const id = Number(resourceId);
	if (!Number.isInteger(id)) {
		return getSelectedResourceIds();
	}

	const current = getSelectedResources();
	const exists = current.some((item) => item.id === id);
	const next = exists
		? current.filter((item) => item.id !== id)
		: [...current, { id, comment: "" }];

	return setSelectedResources(next).map((item) => item.id);
};

export const clearSelectedResourceIds = () => {
	localStorage.removeItem(INQUIRY_SELECTION_KEY);
	emitSelectionChanged([]);
};

export const INQUIRY_SELECTION_EVENT = INQUIRY_SELECTION_CHANGED_EVENT;
